import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/ui/data-table";
import { Trash2, Plus, X } from "lucide-react";
import { formatDate } from "@/utils/formatters";
import adminAgencyRewardsService, {
  AgencyRewardConfigurationRule,
} from "@/services/admin/agencyRewardConfiguration.admin.service";

interface FormState {
  reward: string;
  rewardTypes: string[];
  revenueThreshold: string;
  isActive: boolean;
}

export default function AgencyRewardConfiguration() {
  const [rules, setRules] = useState<AgencyRewardConfigurationRule[]>([]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<AgencyRewardConfigurationRule | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newRewardType, setNewRewardType] = useState("");

  const [form, setForm] = useState<FormState>({
    reward: "",
    rewardTypes: [],
    revenueThreshold: "",
    isActive: true,
  });

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const items = await adminAgencyRewardsService.list();
      setRules(items);
      setIsLoading(false);
    };
    load();
  }, []);

  const resetForm = () =>
    setForm({
      reward: "",
      rewardTypes: [],
      revenueThreshold: "",
      isActive: true,
    });

  const isFormValid =
    form.reward &&
    form.rewardTypes.length > 0 &&
    form.revenueThreshold &&
    Number(form.revenueThreshold) > 0;

  const handleCreate = async () => {
    if (!isFormValid) return;
    setSaving(true);
    try {
      const payload = {
        reward: form.reward,
        rewardTypes: form.rewardTypes,
        revenueThreshold: Number(form.revenueThreshold),
        isActive: form.isActive,
      };
      const created = await adminAgencyRewardsService.create(payload);
      setRules((prev) => [created, ...prev]);
      resetForm();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await adminAgencyRewardsService.remove(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleToggleActive = async (id: string, isActive: boolean) => {
    setTogglingId(id);
    try {
      const updated = await adminAgencyRewardsService.update(id, {
        isActive,
      });
      setRules((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } finally {
      setTogglingId(null);
    }
  };

  const addRewardType = () => {
    if (
      newRewardType.trim() &&
      !form.rewardTypes.includes(newRewardType.trim())
    ) {
      setForm((prev) => ({
        ...prev,
        rewardTypes: [...prev.rewardTypes, newRewardType.trim()],
      }));
      setNewRewardType("");
    }
  };

  const removeRewardType = (typeToRemove: string) => {
    setForm((prev) => ({
      ...prev,
      rewardTypes: prev.rewardTypes.filter((type) => type !== typeToRemove),
    }));
  };

  const columns = useMemo<ColumnDef<AgencyRewardConfigurationRule>[]>(
    () => [
      {
        accessorKey: "reward",
        header: "Reward",
        meta: { width: "w-44" },
        cell: ({ row }) => (
          <span className="font-medium text-foreground">
            {row.original.reward}
          </span>
        ),
      },
      {
        id: "rewardTypes",
        header: "Reward types",
        meta: { width: "w-64" },
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1 py-1">
            {row.original.rewardTypes.map((type, index) => (
              <span
                key={index}
                className="bg-blue-100 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded-md text-xs"
              >
                {type}
              </span>
            ))}
          </div>
        ),
      },
      {
        accessorKey: "revenueThreshold",
        header: "Revenue threshold",
        meta: { width: "w-40" },
        cell: ({ row }) => (
          <span className="text-foreground">
            ${row.original.revenueThreshold.toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: "updatedAt",
        header: "Last updated",
        meta: { width: "w-36" },
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.updatedAt ? formatDate(row.original.updatedAt) : "—"}
          </span>
        ),
      },
      {
        id: "active",
        header: "Active",
        meta: { width: "w-36" },
        cell: ({ row }) => {
          const rule = row.original;
          return (
            <div className="flex items-center gap-2">
              <Switch
                checked={rule.isActive}
                onCheckedChange={(checked) =>
                  handleToggleActive(rule.id, checked)
                }
                disabled={togglingId === rule.id}
              />
              <span
                className={`text-sm font-medium ${
                  rule.isActive
                    ? "text-green-600 dark:text-green-400"
                    : "text-muted-foreground"
                }`}
              >
                {rule.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        meta: {
          width: "w-20",
          headerClassName: "text-right",
          cellClassName: "text-right",
        },
        cell: ({ row }) => {
          const rule = row.original;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteTarget(rule)}
              disabled={deletingId === rule.id}
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
            >
              {deletingId === rule.id ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [togglingId, deletingId],
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Create Agency Reward Rules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Reward Name */}
            <div>
              <Label htmlFor="reward">Reward Name</Label>
              <Input
                id="reward"
                type="text"
                placeholder="Reward Name"
                value={form.reward}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({
                    ...f,
                    reward: e.target.value,
                  }))
                }
                className="w-full"
              />
            </div>

            {/* Reward Types */}
            <div>
              <Label>Reward Types</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Add reward type (e.g., gift card, bonus, commission)"
                    value={newRewardType}
                    onChange={(e) => setNewRewardType(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addRewardType()}
                  />
                  <Button
                    type="button"
                    onClick={addRewardType}
                    className="px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {form.rewardTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {form.rewardTypes.map((type, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 bg-blue-100 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-md text-sm"
                      >
                        <span>{type}</span>
                        <button
                          type="button"
                          onClick={() => removeRewardType(type)}
                          className="hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Revenue Threshold */}
            <div>
              <Label htmlFor="threshold">Revenue Threshold ($)</Label>
              <Input
                id="threshold"
                type="text"
                inputMode="numeric"
                placeholder="Revenue Threshold"
                value={form.revenueThreshold}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.replace(/[^0-9.]/g, "");
                  setForm((f) => ({
                    ...f,
                    revenueThreshold: value,
                  }));
                }}
                className="w-full"
              />
            </div>

            {/* Is Active */}
            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={form.isActive}
                onCheckedChange={(checked) =>
                  setForm((f) => ({
                    ...f,
                    isActive: checked,
                  }))
                }
              />
              <Label htmlFor="isActive">Active</Label>
            </div>

            <Button
              className="bg-[#d7785b] hover:bg-[#c66a4f] w-32 text-white"
              disabled={!isFormValid || saving}
              onClick={handleCreate}
            >
              {saving ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </div>
              ) : (
                "Create Reward"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>Agency Rewards</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable
              columns={columns}
              data={rules}
              isLoading={isLoading}
              skeletonRows={4}
              getRowId={(row) => row.id}
              emptyText="No Agency Rewards yet"
            />
          </CardContent>
        </Card>
      </div>

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            handleDelete(deleteTarget.id);
          }
          setDeleteTarget(null);
        }}
        title="Delete Agency Reward"
        message="Are you sure you want to delete this agency reward? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteTarget ? deletingId === deleteTarget.id : false}
      />
    </div>
  );
}
